import { useState, useEffect } from "react";

// types
import { AnnualRevenue } from "types/filters-store.types";

// components
import {
  FilterFlyout,
  Radio,
  RoundedButton,
} from "components/LibraryComponents/Components";
import { TextInput } from "@trussworks/react-uswds";
import { PigIcon } from "components/icons";

// constants
import {
  FilterKeys,
  FilterTitles,
  RelationOperatorType,
} from "constants/filters";

// store
import useFiltersStore from "store/useFiltersStoreNew";

// hooks
import useDebounce from "hooks/useDebounce";

// styles
import styles from "./AnnualRevenueFilterButton.module.scss";

export default function AnnualRevenueFilterButton() {
  const { annualRevenue, setFilter } = useFiltersStore((state) => ({
    annualRevenue: state.filters.annualRevenue,
    setFilter: state.setFilter,
  }));

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>(annualRevenue.annualGrossRevenue);
  // This state only control if filter is open,
  // and has more than one value apply max-width to filter button.
  const [isHalt, setIsHalt] = useState<boolean>(true);

  const debouncedValue = useDebounce(value, 350);

  const selectedValue = getSelectedValue(annualRevenue);
  const isDisabled = selectedValue.length === 0;

  // check if filter is open and has more than one value
  useEffect(() => {
    if (selectedValue.length === 0) {
      if (!isHalt) setIsHalt(true);
    }
  }, [selectedValue]);

  // set annual gross revenue by debounced value
  useEffect(() => {
    if (!debouncedValue) {
      return;
    }

    setFilter(FilterKeys.ANNUAL_REVENUE, {
      ...annualRevenue,
      annualGrossRevenue: debouncedValue,
    });
  }, [debouncedValue]);

  // set value from filter store on mount, if value is not empty
  useEffect(() => {
    if (value !== "" && annualRevenue.annualGrossRevenue === "") {
      setValue("");
    }
  }, [annualRevenue.annualGrossRevenue]);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;

    setFilter(FilterKeys.ANNUAL_REVENUE, {
      ...annualRevenue,
      relationOperator: id,
    });
  };

  const handleAnnualGrossRevenueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    if (value && Number(value) <= 0) {
      return;
    }

    setValue(value);
  };

  const handleCallback = (open: boolean) => {
    if (!open && annualRevenue.annualGrossRevenue !== "") {
      setIsHalt(false);
    }
  };

  const handleClear = () => {
    setFilter(FilterKeys.ANNUAL_REVENUE, {
      relationOperator: RelationOperatorType.AT_LEAST,
      annualGrossRevenue: "",
    });

    setValue("");
  };

  return (
    <FilterFlyout
      id={FilterKeys.ANNUAL_REVENUE}
      filterName={FilterTitles[FilterKeys.ANNUAL_REVENUE]}
      title={"Annual gross revenue"}
      FilterIcon={<PigIcon aria-label="Annual Revenue Filter Icon" />}
      options={selectedValue}
      isOpen={isOpen}
      isHalt={isHalt}
      setIsOpen={setIsOpen}
      callback={handleCallback}
    >
      <div className={styles["annual-revenue-filter-content"]}>
        <div className={styles["radio-group"]}>
          <Radio
            id={RelationOperatorType.AT_LEAST}
            name={RelationOperatorType.AT_LEAST}
            value={RelationOperatorType.AT_LEAST}
            label="At least"
            checked={
              annualRevenue.relationOperator === RelationOperatorType.AT_LEAST
            }
            onChange={handleRadioChange}
          />
          <Radio
            id={RelationOperatorType.NO_MORE}
            name={RelationOperatorType.NO_MORE}
            value={RelationOperatorType.NO_MORE}
            label="No more than"
            checked={
              annualRevenue.relationOperator === RelationOperatorType.NO_MORE
            }
            onChange={handleRadioChange}
          />
        </div>

        <div className={styles["input-group"]}>
          <label htmlFor="annualGrossRevenue">
            Annual gross revenue amount
          </label>
          <div className={styles["input-wrapper"]}>
            <TextInput
              id="annualGrossRevenue"
              name="annualGrossRevenue"
              type="number"
              placeholder="Type a dollar amount"
              value={value}
              onChange={handleAnnualGrossRevenueChange}
            />
          </div>
        </div>

        <div className={styles["clear-filter"]}>
          <RoundedButton
            variant="ghost"
            onClick={handleClear}
            disabled={isDisabled}
          >
            Clear filter selections
          </RoundedButton>
        </div>
      </div>
    </FilterFlyout>
  );
}

function getSelectedValue(annualRevenue: AnnualRevenue) {
  if (annualRevenue.annualGrossRevenue !== "") {
    const numOfEmps = Number(annualRevenue.annualGrossRevenue).toLocaleString(
      "en-US"
    );

    if (annualRevenue.relationOperator === RelationOperatorType.AT_LEAST) {
      return [`≥ $${numOfEmps}`];
    } else {
      return [`≤ $${numOfEmps}`];
    }
  }

  return [];
}
